import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import { blue, red } from '@mui/material/colors';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import InfoIcon from '@mui/icons-material/Info';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';

// Create theme
const theme = createTheme({
  direction: 'rtl',
  palette: {
    primary: {
      main: blue[700],
    },
    error: {
      main: red[500],
    },
    success: {
      main: '#4caf50',
    },
  },
  typography: {
    fontFamily: 'Rubik, Arial, sans-serif',
  },
});


const InterestRatesSchedule = () => {
  const [hoveredRow, setHoveredRow] = useState(null);
  
  const scheduleData = [
    {
      reportDate: '31 בינואר',
      lastReportDate: '6 בפברואר (יום חמישי)',
      publishDate: '12 בפברואר (יום רביעי)',
      notes: 'יום הפרסום נדחה'
    },
    {
      reportDate: '28 בפברואר',
      lastReportDate: '5 במרץ (יום רביעי)',
      publishDate: '11 במרץ (יום שלישי)',
      notes: 'יום הפרסום נדחה'
    },
    {
      reportDate: '31 במרץ',
      lastReportDate: '3 באפריל (יום חמישי)',
      publishDate: '10 באפריל (יום חמישי)',
      notes: ''
    },
    {
      reportDate: '30 באפריל',
      lastReportDate: '7 במאי (יום רביעי)',
      publishDate: '14 במאי (יום רביעי)',
      notes: 'יום הפרסום נדחה'
    },
    {
      reportDate: '31 במאי',
      lastReportDate: '8 ביוני (יום ראשון)',
      publishDate: '15 ביוני (ראשון)',
      notes: 'יום הפרסום נדחה'
    },
    {
      reportDate: '30 ביוני',
      lastReportDate: '6 ביולי (יום ראשון)',
      publishDate: '14 ביולי (יום שני)',
      notes: 'יום הפרסום נדחה'
    },
    {
      reportDate: '31 ביולי',
      lastReportDate: '7 באוגוסט (יום חמישי)',
      publishDate: '13 באוגוסט (רביעי)',
      notes: 'יום הפרסום נדחה'
    },
    {
      reportDate: '31 באוגוסט',
      lastReportDate: '4 בספטמבר (יום חמישי)',
      publishDate: '10 בספטמבר (רביעי)',
      notes: ''
    },
    {
      reportDate: '30 בספטמבר',
      lastReportDate: '16 באוקטובר (יום חמישי)',
      publishDate: '23 באוקטובר (חמישי)',
      notes: 'יום הפרסום נדחה'
    },
    {
      reportDate: '31 באוקטובר',
      lastReportDate: '5 בנובמבר (יום רביעי)',
      publishDate: '11 בנובמבר (שלישי)',
      notes: 'יום הפרסום נדחה'
    },
    {
      reportDate: '30 בנובמבר',
      lastReportDate: '4 בדצמבר (יום חמישי)',
      publishDate: '10 בדצמבר (רביעי)',
      notes: ''
    },
    {
      reportDate: '31 בדצמבר',
      lastReportDate: '7 בינואר 2026 (יום רביעי)',
      publishDate: '13 בינואר 2026 (שלישי)',
      notes: 'יום הפרסום נדחה'
    }
  ];

  const infoCards = [
    {
      icon: <CalendarMonthIcon sx={{ fontSize: '2rem', color: blue[700] }} />,
      title: 'תאריכי דיווח',
      description: 'מועדי הדיווח החודשיים לשנת 2025'
    },
    {
      icon: <EventAvailableIcon sx={{ fontSize: '2rem', color: '#4caf50' }} />,
      title: 'מועדי פרסום',
      description: 'תאריכי פרסום הריבית באתר הבנק'
    }
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{
        maxWidth: '1200px',
        margin: '40px auto',
        padding: '0 20px',
        direction: 'rtl'
      }}>
        <Box sx={{
          textAlign: 'center',
          marginBottom: '40px',
          direction: 'rtl',
          width: '100%'
        }}>
          <Box sx={{ 
            mb: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%'
          }}>
            <Typography variant="h4" fontWeight="bold" sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              justifyContent: 'center',
              '& svg': {
                order: -1
              }
            }}>
              <CalendarMonthIcon sx={{ color: blue[700], fontSize: '2.5rem' }} />
              לוח מועדי פרסום ריביות 2025
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ 
              mt: 2,
              textAlign: 'center',
              maxWidth: '600px'
            }}>
              מועדי פרסום הריבית הממוצעת לצורך חישוב עמלת פירעון מוקדם של משכנתאות
            </Typography>
          </Box>

          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)'
            },
            gap: '20px',
            margin: '2rem 0'
          }}>
            {infoCards.map((card, index) => (
              <Card 
                key={index}
                sx={{
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
                  },
                  cursor: 'pointer'
                }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  {card.icon}
                  <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {card.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        <TableContainer 
          component={Paper} 
          sx={{ 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderRadius: '12px',
            overflow: 'hidden',
            mb: 4,
            direction: 'rtl'
          }}
        >
          <Table dir="rtl" sx={{ 
            minWidth: { xs: '100%', sm: '800px' },
            borderCollapse: 'collapse',
          }}>
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{
                    backgroundColor: blue[700],
                    color: 'white',
                    fontWeight: 'bold',
                    padding: '16px',
                    fontSize: '1rem',
                    textAlign: 'right',
                    borderLeft: '1px solid rgba(255, 255, 255, 0.2)'
                  }}
                >
                  תאריך הדיווח
                  <Tooltip title="התאריך בו מתפרסם הדיווח" placement="top">
                    <IconButton size="small" sx={{ 
                      color: 'white',
                      marginRight: '8px',
                      padding: '4px',
                    }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell 
                  sx={{
                    backgroundColor: blue[700],
                    color: 'white',
                    fontWeight: 'bold',
                    padding: '16px',
                    fontSize: '1rem',
                    textAlign: 'right',
                    borderLeft: '1px solid rgba(255, 255, 255, 0.2)'
                  }}
                >
                  מועד אחרון לדיווח
                  <Tooltip title="המועד האחרון להגשת הדיווח" placement="top">
                    <IconButton size="small" sx={{ 
                      color: 'white',
                      marginRight: '8px',
                      padding: '4px',
                    }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell 
                  sx={{
                    backgroundColor: blue[700],
                    color: 'white',
                    fontWeight: 'bold',
                    padding: '16px',
                    fontSize: '1rem',
                    textAlign: 'right',
                    borderLeft: '1px solid rgba(255, 255, 255, 0.2)'
                  }}
                >
                  מועד פרסום הריבית
                  <Tooltip title="מועד פרסום הריבית באתר הבנק" placement="top">
                    <IconButton size="small" sx={{ 
                      color: 'white',
                      marginRight: '8px',
                      padding: '4px',
                    }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell 
                  sx={{
                    backgroundColor: blue[700],
                    color: 'white',
                    fontWeight: 'bold',
                    padding: '16px',
                    fontSize: '1rem',
                    textAlign: 'right'
                  }}
                >
                  הערות
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scheduleData.map((row, index) => (
                <TableRow
                  key={index}
                  onMouseEnter={() => setHoveredRow(index)}
                  onMouseLeave={() => setHoveredRow(null)}
                  sx={{
                    backgroundColor: hoveredRow === index ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
                    '&:nth-of-type(odd)': {
                      backgroundColor: hoveredRow === index ? 'rgba(0, 0, 0, 0.04)' : 'rgba(0, 0, 0, 0.02)'
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    },
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  <TableCell sx={{ 
                    textAlign: 'right',
                    padding: '16px',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    borderLeft: '1px solid rgba(224, 224, 224, 1)'
                  }}>
                    {row.reportDate}
                  </TableCell>
                  <TableCell sx={{ 
                    textAlign: 'right',
                    padding: '16px',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    borderLeft: '1px solid rgba(224, 224, 224, 1)'
                  }}>
                    {row.lastReportDate}
                  </TableCell>
                  <TableCell sx={{ 
                    textAlign: 'right',
                    padding: '16px',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    borderLeft: '1px solid rgba(224, 224, 224, 1)'
                  }}>
                    {row.publishDate}
                  </TableCell>
                  <TableCell sx={{ 
                    textAlign: 'right',
                    padding: '16px',
                    fontSize: '0.95rem'
                  }}>
                    <Typography 
                      component="span" 
                      sx={{ 
                        color: row.notes ? red[500] : 'inherit',
                        fontWeight: row.notes ? 500 : 'normal'
                      }}
                    >
                      {row.notes}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </ThemeProvider>
  );
};




export default InterestRatesSchedule; 